import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MarketplaceShell from './MarketplaceShell';
import StepIndicator from './StepIndicator';
import { UseCaseCard, UseCaseCardSkeleton, UseCaseTile, USE_CASE_META } from './useCaseMeta';
import { MktCard, MktButton, Eyebrow, Pill, Tag, Checkbox, PriceTag, BackLink } from './marketplaceUi';
import {
  getUseCases, getSubCategories, getArchetypes, getMarketplaceState,
  dollars, type UseCase, type SubCategory, type Archetype, type StudySelection,
} from './marketplaceApi';

/**
 * Paid study — ported from pay_per_study.html. Two internal screens:
 * (1) choose a use case, (2) build an audience by selecting sub-categories and
 * archetypes (checkbox model — each selected archetype contributes all its
 * Twins). Cost = Σ selected Twins × unit price.
 */
const BuildAudienceScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preset = (location.state || {}) as { useCase?: string };
  // Deep-linked with a use case (e.g. a returning user from a dashboard card) →
  // skip straight to the audience builder.
  const [screen, setScreen] = useState<1 | 2>(preset.useCase ? 2 : 1);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [subs, setSubs] = useState<SubCategory[]>([]);
  const [unitPrice, setUnitPrice] = useState(500);
  const [useCase, setUseCase] = useState<string | null>(preset.useCase || null);
  const [activeCats, setActiveCats] = useState<string[]>([]);
  const [cache, setCache] = useState<Record<string, Archetype[]>>({});
  // sub-category slug -> set of selected archetype_ids
  const [sel, setSel] = useState<Record<string, Set<string>>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getUseCases(), getSubCategories(), getMarketplaceState()])
      .then(([u, s, st]) => {
        setUseCases(u); setSubs(s); setUnitPrice(st.twin_unit_price_cents);
        if (s[0]) loadCat(s[0].slug, true); // pre-activate the first sub-category
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMeta(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCat = (slug: string, activate: boolean) => {
    if (activate) setActiveCats((c) => (c.includes(slug) ? c : [...c, slug]));
    if (!cache[slug]) {
      getArchetypes(slug)
        .then((a) => setCache((c) => ({ ...c, [slug]: a })))
        .catch((e) => setError(e.message));
    }
  };

  const toggleCat = (slug: string) => {
    if (activeCats.includes(slug)) {
      setActiveCats((c) => c.filter((s) => s !== slug));
      setSel((s) => { const n = { ...s }; delete n[slug]; return n; });
    } else {
      loadCat(slug, true);
      setSel((s) => ({ ...s, [slug]: s[slug] || new Set() }));
    }
  };

  // Start with a non-zero selection (mockup never shows "0 selected"): once the
  // pre-activated sub-category's archetypes load, select its first archetype.
  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current) return;
    const first = activeCats[0];
    const archs = first ? cache[first] : undefined;
    if (!first || !archs || archs.length === 0) return;
    preselected.current = true;
    setSel((s) => (s[first] && s[first].size > 0 ? s : { ...s, [first]: new Set([archs[0].archetype_id]) }));
  }, [cache, activeCats]);

  const archsOf = (slug: string) => cache[slug] || [];
  const isAll = (slug: string) => {
    const a = archsOf(slug);
    return a.length > 0 && a.every((x) => sel[slug]?.has(x.archetype_id));
  };
  const toggleAll = (slug: string) => {
    setSel((s) => {
      const next = { ...s };
      next[slug] = isAll(slug) ? new Set() : new Set(archsOf(slug).map((a) => a.archetype_id));
      return next;
    });
  };
  const toggleArch = (slug: string, id: string) => {
    setSel((s) => {
      const set = new Set(s[slug] || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...s, [slug]: set };
    });
  };

  const totals = useMemo(() => {
    let twins = 0;
    const tags: { label: string; n: number }[] = [];
    const selections: StudySelection[] = [];
    activeCats.forEach((slug) => {
      const archs = archsOf(slug);
      const set = sel[slug];
      if (!set || set.size === 0) return;
      const label = subs.find((s) => s.slug === slug)?.name || slug;
      if (isAll(slug)) {
        const n = archs.reduce((acc, a) => acc + a.twin_count, 0);
        tags.push({ label: `${label} · All`, n });
      }
      archs.forEach((a) => {
        if (!set.has(a.archetype_id)) return;
        twins += a.twin_count;
        selections.push({ sub_category: slug, archetype_id: a.archetype_id, twin_count: a.twin_count, name: a.name });
        if (!isAll(slug)) tags.push({ label: a.name, n: a.twin_count });
      });
    });
    return { twins, cost: twins * unitPrice, tags, selections };
  }, [activeCats, sel, cache, subs, unitPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  const canStart = !!useCase && totals.twins > 0;
  const meta = useCase ? USE_CASE_META[useCase] : null;

  const onStart = () => {
    if (!canStart) return;
    navigate('/marketplace/run/fill', { state: { useCase, selections: totals.selections, paid: true } });
  };

  if (error) return <MarketplaceShell><div className="text-red-600">{error}</div></MarketplaceShell>;

  return (
    <MarketplaceShell>
      <StepIndicator step={screen} labels={['Choose use case', 'Build your audience']} />

      {/* ── Screen 1: use case (flow entry — no back affordance) ────── */}
      {screen === 1 && (
        <>
          <div className="mb-7 flex items-start justify-between">
            <div>
              <Eyebrow className="mb-1.5">New study · Pay per Twin</Eyebrow>
              <h1 className="text-[22px] font-medium text-[#111]">What kind of study do you want to run?</h1>
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">Pick one use case. You can run a different one next time.</p>
            </div>
            <PriceTag />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {loadingMeta
              ? Array.from({ length: 4 }).map((_, i) => <UseCaseCardSkeleton key={i} />)
              : useCases.map((uc) => (
                  <UseCaseCard key={uc.key} useCaseKey={uc.key} selected={useCase === uc.key} onClick={() => setUseCase(uc.key)} />
                ))}
          </div>

          <div className="mt-8 flex justify-end">
            <MktButton disabled={!useCase} onClick={() => setScreen(2)}>Next — build your audience →</MktButton>
          </div>
        </>
      )}

      {/* ── Screen 2: audience builder ─────────────────────────────── */}
      {screen === 2 && meta && (
        <>
          <BackLink label="Choose use case" onClick={() => setScreen(1)} className="mb-3" />
          <div className="mb-5 flex items-start justify-between">
            <div>
              <Eyebrow className="mb-1.5">New study · Pay per Twin</Eyebrow>
              <h1 className="text-[22px] font-medium text-[#111]">Build your audience</h1>
              <p className="mt-1.5 text-[13px] text-[#6b6b6b]">Select sub-categories and archetypes. Cost updates as you build.</p>
            </div>
            <PriceTag />
          </div>

          {/* Selected use case summary (back is the single BackLink above). */}
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-black/10 bg-white px-4 py-3">
            <UseCaseTile useCaseKey={useCase!} />
            <div>
              <div className="text-[11px] text-[#6b6b6b]">Selected use case</div>
              <div className="text-[14px] font-medium text-[#111]">{meta.title}</div>
            </div>
          </div>

          <MktCard className="p-5">
            <div className="mb-2.5 flex items-baseline justify-between">
              <div className="text-[12px] text-[#6b6b6b]">Sub-category</div>
              <div className="text-[12px] text-[#6b6b6b]">Select one or more</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {subs.map((sc) => (
                <Pill key={sc.slug} active={activeCats.includes(sc.slug)} showCheck onClick={() => toggleCat(sc.slug)}>
                  {sc.name}
                </Pill>
              ))}
            </div>

            <div className="my-5 h-px bg-black/10" />

            {/* Per-category archetype blocks */}
            <div className="mb-6 flex flex-col gap-2.5">
              {activeCats.map((slug) => {
                const archs = archsOf(slug);
                const set = sel[slug] || new Set();
                const label = subs.find((s) => s.slug === slug)?.name || slug;
                const total = archs.reduce((n, a) => n + a.twin_count, 0);
                const selTwins = archs.reduce((n, a) => n + (set.has(a.archetype_id) ? a.twin_count : 0), 0);
                const allSel = isAll(slug);
                return (
                  <div key={slug} className="overflow-hidden rounded-xl border border-black/10">
                    <div className="flex items-center justify-between border-b border-black/10 bg-[#f5f5f3] px-4 py-3">
                      <div className="text-[13px] font-medium text-[#111]">{label}</div>
                      <div className="text-[12px] text-[#6b6b6b]">
                        {set.size} of {archs.length} selected · <span className="font-medium text-[#111]">{selTwins} Twins</span>
                      </div>
                    </div>
                    {/* All row */}
                    <div
                      onClick={() => toggleAll(slug)}
                      className={`flex cursor-pointer items-center justify-between border-b border-black/10 px-4 py-2.5 ${allSel ? 'bg-[#E8FCFA]' : 'bg-[#f5f5f3] hover:bg-[#ececea]'}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <Checkbox checked={allSel} />
                        <span className="text-[13px] font-medium text-[#111]">All archetypes</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] text-[#6b6b6b]">{total} Twins</span>
                        <span className="text-[12px] font-medium text-[#6b6b6b]">{dollars(total * unitPrice)}</span>
                      </div>
                    </div>
                    {/* Archetype rows */}
                    {archs.map((a) => {
                      const checked = set.has(a.archetype_id);
                      return (
                        <div
                          key={a.archetype_id}
                          onClick={() => toggleArch(slug, a.archetype_id)}
                          className={`flex cursor-pointer items-center justify-between border-b border-black/10 px-4 py-2.5 last:border-b-0 ${checked ? 'bg-[#E8FCFA]' : 'hover:bg-[#f5f5f3]'}`}
                        >
                          <div className="flex items-center gap-2.5">
                            <Checkbox checked={checked} />
                            <span className="text-[13px] text-[#111]">{a.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[12px] text-[#6b6b6b]">{a.twin_count} Twins</span>
                            <span className="text-[12px] font-medium text-[#6b6b6b]">{dollars(a.twin_count * unitPrice)}</span>
                          </div>
                        </div>
                      );
                    })}
                    {archs.length === 0 && (
                      <div className="px-4 py-3 text-[13px] text-[#999]">Loading archetypes…</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Checkout bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-black/10 p-4">
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <div className="text-[11px] text-[#6b6b6b]">Twins selected</div>
                  <div className="text-[22px] font-medium text-[#111]">{totals.twins}</div>
                </div>
                <div className="h-8 w-px bg-black/10" />
                <div>
                  <div className="text-[11px] text-[#6b6b6b]">Total cost</div>
                  <div className="text-[22px] font-medium text-[#111]">{dollars(totals.cost)}</div>
                </div>
                <div className="flex max-w-[220px] flex-wrap gap-1">
                  {totals.tags.slice(0, 3).map((t, i) => (
                    <Tag key={i}>{t.label} · {t.n}</Tag>
                  ))}
                  {totals.tags.length > 3 && <Tag>+{totals.tags.length - 3} more</Tag>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <MktButton disabled={!canStart} onClick={onStart}>Start study →</MktButton>
                {totals.twins === 0 && (
                  <div className="text-[11px] text-[#6b6b6b]">Select at least one archetype to continue</div>
                )}
              </div>
            </div>
          </MktCard>
        </>
      )}
    </MarketplaceShell>
  );
};

export default BuildAudienceScreen;
